import { AdvancedFilter } from '@cldsystems/e3-fe-lib';
import {
  useCompanyGetById,
  usePriceListGetList,
  WebshopPriceList,
} from '@cldsystems/ws-api-client';
import { useCallback } from 'react';
import { useKeycloak } from '@/providers/keycloak-provider';
import { useStore } from '@/store/zustand/zustand.setup';

type ProductWithPrice = {
  id?: string;
  price: number;
  discountedPrice: number;
};

export type DiscountType = 'NONE' | 'PRODUCT' | 'COMPANY' | 'PRICE_LIST';

/**
 * Hook that provides a function to check if a product has a discount and what type.
 * Takes into account three sources, in priority order:
 *  1. PRICE_LIST – a company-scoped price list with `discountedPrice < price`.
 *     Wins over everything else because it is a directly-negotiated price.
 *  2. COMPANY    – category-level discount on the user's company.
 *  3. PRODUCT    – product-level catalog discount (`product.discountedPrice < product.price`).
 *
 * @returns A function that returns the discount type: NONE, PRODUCT, COMPANY or PRICE_LIST
 */
export const useGetDiscountType = () => {
  const { keycloak } = useKeycloak();
  const companyId = useStore(state => state.company.companyId);
  const isAuthenticatedWithCompany = Boolean(keycloak?.authenticated) && Boolean(companyId);

  const { data: companyData } = useCompanyGetById({
    params: { id: companyId ?? '' },
    enabled: isAuthenticatedWithCompany,
    staleTime: Infinity,
  });

  const advancedFilter: AdvancedFilter<WebshopPriceList> = {
    OR: [
      {
        'companies.id': {
          operator: 'contains',
          value: companyId,
        },
      },
    ],
  };

  const { data: priceList } = usePriceListGetList({
    params: { from: '0', size: '100' },
    queries: { advancedFilter: JSON.stringify(advancedFilter) },
    enabled: isAuthenticatedWithCompany,
    staleTime: Infinity,
  });

  /**
   * Checks if a product has a discount and returns the discount type.
   *
   * @param product - Product with price and discountedPrice
   * @param categoryId - Optional category ID to check for category-specific discounts
   * @param productId - Optional product ID for price-list lookup (use when product object has no id)
   * @returns DiscountType - "NONE" | "PRODUCT" | "COMPANY" | "PRICE_LIST"
   *
   * - NONE: No discount available
   * - PRICE_LIST: A company-scoped price list provides a `discountedPrice` lower than its `price`
   * - COMPANY: Company has a category discount for this product
   * - PRODUCT: Product has a built-in discount (price > discountedPrice)
   */
  const getDiscountType = useCallback(
    (product: ProductWithPrice, categoryId?: string, productId?: string): DiscountType => {
      const id = productId ?? product.id;
      const productPriceFromPriceList = priceList?.items[0]?.products?.find(
        ({ id: listId }) => listId === id,
      );
      if (productPriceFromPriceList) {
        // A price-list entry replaces the catalog price entirely (see
        // `FormattedPrice`) and short-circuits company / product discounts.
        // Treat it as a discount only when the list itself defines a
        // `discountedPrice` below `price`; otherwise it is just a flat
        // negotiated price and should render as `NONE`.
        if (
          productPriceFromPriceList.discountedPrice > 0 &&
          productPriceFromPriceList.discountedPrice < productPriceFromPriceList.price
        ) {
          return 'PRICE_LIST';
        }
        return 'NONE';
      }
      const categoryDiscount = companyData?.company.categoryDiscounts.find(
        discount => discount.id === categoryId,
      );

      if (categoryDiscount && categoryDiscount.discount !== 0) {
        return 'COMPANY';
      }

      if (product.price > product.discountedPrice) {
        return 'PRODUCT';
      }

      return 'NONE';
    },
    [companyData, priceList],
  );

  return { getDiscountType };
};
